/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { SemanticModel, SemanticModelDimension, SemanticModelFact, SemanticModelLogicalTable, SemanticModelTimeDimension, SnowflakeDatabaseSchema } from '../../../../../api/initiative-data-integrations';

// Filter out DataIntegration system columns
export const isDataIntegrationSystemColumn = (columnName: string): boolean => {
  return columnName.toUpperCase().startsWith('_FIVETRAN_');
};

export const filterDataIntegrationColumns = <T extends { name: string }>(columns: T[]): T[] => {
  return columns.filter(column => !isDataIntegrationSystemColumn(column.name));
};

export const cleanSemanticModelFromDataIntegrationColumns = (model: SemanticModel): SemanticModel => {
  return {
    ...model,
          tables: model.tables.map(table => ({
        ...table,
        dimensions: table.dimensions ? filterDataIntegrationColumns(table.dimensions) : [],
        time_dimensions: table.time_dimensions ? filterDataIntegrationColumns(table.time_dimensions) : [],
        facts: table.facts ? filterDataIntegrationColumns(table.facts) : [],
    }))
  };
};

const DEFAULTS = {
  CUSTOM_INSTRUCTIONS: `You are a sustainability and governance data assistant helping a company understand and analyze their sustainability, governance, and compliance metrics.
  You can answer questions related to environmental (emissions, water use, energy, waste, etc.), governance (board composition, anti-corruption policies, training), and compliance data.
  If a question is ambiguous, ask for clarification rather than guessing.`,
};

export const convertSchemaToSemanticModel = (schema: SnowflakeDatabaseSchema | undefined) => {

  const basicModel: SemanticModel = {
    name: 'Sustainability_data_model',
    description: '',
    custom_instructions: DEFAULTS.CUSTOM_INSTRUCTIONS,
    module_custom_instructions: {},
    tables: [],
    relationships: [],
  };

  if (!schema) return basicModel;

  // Add tables from schema
  Object.entries(schema.schemas).forEach(([schemaName, schemaData]) => {
    Object.entries(schemaData.tables).forEach(([tableName, tableData]) => {
      const logicalTable: SemanticModelLogicalTable = {
        name: tableName,
        base_table: {
          database: schema.database_name,
          schema: schemaName,
          table: tableName,
        },
        description: tableData.description || '',
        synonyms: [],
        dimensions: [],
        time_dimensions: [],
        facts: [],
        metrics: [],
        filters: [],
      };

      // Add columns as dimensions (excluding DataIntegration system columns)
      Object.entries(tableData.columns).forEach(([columnName, columnData]) => {
        // Skip DataIntegration system columns
        if (isDataIntegrationSystemColumn(columnName)) {
          return;
        }

        // Categorize columns
        if (isTimeColumn(columnName, columnData.data_type)) {
          const column: SemanticModelTimeDimension = {
            name: columnName,
            expr: columnName,
            description: columnData.description || columnName,
            data_type: mapDataTypeToSemanticType(columnData.data_type),
            synonyms: [],
            unique: false,
          };
          logicalTable.time_dimensions?.push(column);
        } else if (isFactColumn(columnName, columnData.data_type)) {
          const column: SemanticModelFact = {
            name: columnName,
            expr: columnName,
            description: columnData.description || columnName,
            data_type: mapDataTypeToSemanticType(columnData.data_type),
            synonyms: [],
          };
          logicalTable.facts?.push(column);
        } else {
          const column: SemanticModelDimension = {
            name: columnName,
            expr: columnName,
            description: columnData.description || columnName,
            data_type: mapDataTypeToSemanticType(columnData.data_type),
            synonyms: [],
            unique: false,
          };

          logicalTable.dimensions?.push(column);
        }
      });

      basicModel.tables.push(logicalTable);
    });
  });

  return basicModel;
};

const mapDataTypeToSemanticType = (snowflakeType: string): string => {
  const typeMap: { [key: string]: string } = {
    'NUMBER': 'number',
    'DECIMAL': 'number',
    'FLOAT': 'number',
    'DOUBLE': 'number',
    'INTEGER': 'number',
    'BIGINT': 'number',
    'SMALLINT': 'number',
    'TINYINT': 'number',
    'BOOLEAN': 'boolean',
    'VARCHAR': 'string',
    'STRING': 'string',
    'TEXT': 'string',
    'CHAR': 'string',
    'DATE': 'date',
    'TIMESTAMP': 'datetime',
    'TIME': 'time',
  };

  const baseType = snowflakeType.split('(')[0].toUpperCase();
  return typeMap[baseType] || 'string';
};

const isTimeColumn = (columnName: string, dataType: string): boolean => {
  const timeKeywords = ['date', 'time', 'created', 'updated', 'modified'];
  const lowerName = columnName.toLowerCase();
  const lowerType = dataType.toLowerCase();
  return timeKeywords.some(keyword => lowerName.includes(keyword)) ||
         lowerType.includes('date') || lowerType.includes('time');
};

const isFactColumn = (columnName: string, dataType: string): boolean => {
  const factKeywords = ['amount', 'count', 'total', 'sum', 'value', 'price', 'cost', 'revenue', 'quantity'];
  const lowerName = columnName.toLowerCase();
  const lowerType = dataType.toLowerCase();
  return factKeywords.some(keyword => lowerName.includes(keyword)) ||
         lowerType.includes('number') || lowerType.includes('decimal');
};
